import { useEffect, useMemo, useState } from 'react';
import {
  ComposedModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Checkbox,
  Search,
} from '@carbon/react';
import {
  FILTER_DEFS,
  filterDef,
  makeFilterId,
  buildDifferenceOptions,
  buildCategoricalOptions,
  type FilterKey,
  type PropertyFilter,
} from './property-filters';
import { format0 } from 'utils/number';
import type { EconomicsPair } from './property-attributes';
import type { ComparisonResult } from './types';
import style from './attribute-modal.module.scss';

interface PropertiesFilterModalProps {
  open: boolean;
  onClose: () => void;
  results: ComparisonResult[];
  /** Currently active filters (drafts are seeded from these). */
  filters: PropertyFilter[];
  /** Both sides' economics, so Total Target Value appears as a difference option. */
  economics?: EconomicsPair | null;
  /** Which filter type to focus on open (e.g. from an "edit" action). */
  focusKey?: FilterKey | null;
  onApply: (filters: PropertyFilter[]) => void;
}

/**
 * "Properties Filters" modal (mock v16, opened from "Add Filter"): configure one
 * or more filters — a Difference filter and categorical value filters — then
 * Apply to narrow the Properties list. Filters combine with AND. Seeds from the
 * active filters so it doubles as the editor.
 * @param {PropertiesFilterModalProps} props - open state, results, active filters, handlers
 * @return {JSX.Element} The properties-filter modal
 */
export const PropertiesFilterModal = ({
  open,
  onClose,
  results,
  filters,
  economics = null,
  focusKey = null,
  onApply,
}: PropertiesFilterModalProps) => {
  const [selectedKey, setSelectedKey] = useState<FilterKey>('difference');
  const [nameSearch, setNameSearch] = useState('');
  const [valueSearch, setValueSearch] = useState('');
  // Per filter type: the set of checked difference fields / categorical values.
  const [drafts, setDrafts] = useState<Record<string, Set<string>>>({});

  // Seed a draft per filter type from the active filter (empty otherwise).
  const buildDrafts = useMemo(
    () =>
      (useExisting: boolean): Record<string, Set<string>> => {
        const next: Record<string, Set<string>> = {};
        FILTER_DEFS.forEach((def) => {
          const existing = useExisting
            ? filters.find((f) => f.key === def.key)
            : undefined;
          next[def.key] = new Set(existing?.values ?? []);
        });
        return next;
      },
    [filters]
  );

  useEffect(() => {
    if (!open) return;
    setDrafts(buildDrafts(true));
    setSelectedKey(focusKey ?? 'difference');
    setNameSearch('');
    setValueSearch('');
  }, [open, focusKey, buildDrafts]);

  const def = filterDef(selectedKey);
  const draft = drafts[selectedKey];

  const filterList = useMemo(
    () =>
      FILTER_DEFS.filter((f) =>
        f.label.toLowerCase().includes(nameSearch.trim().toLowerCase())
      ),
    [nameSearch]
  );

  const differenceOptions = useMemo(
    () => buildDifferenceOptions(results, economics),
    [results, economics]
  );
  const categoricalOptions = useMemo(() => {
    return def.kind === 'categorical'
      ? buildCategoricalOptions(results, selectedKey)
      : [];
  }, [results, selectedKey, def.kind]);

  const toggleValue = (value: string, checked: boolean) =>
    setDrafts((prev) => {
      const values = new Set(prev[selectedKey]);
      if (checked) values.add(value);
      else values.delete(value);
      return { ...prev, [selectedKey]: values };
    });

  // Value options for the current filter (difference carries counts).
  const valueOptions = useMemo(() => {
    if (def.kind === 'difference') return differenceOptions;
    return categoricalOptions.map((o) => ({
      ...o,
      count: undefined as number | undefined,
    }));
  }, [def.kind, differenceOptions, categoricalOptions]);

  const visibleOptions = useMemo(() => {
    const q = valueSearch.trim().toLowerCase();
    return q
      ? valueOptions.filter((o) => o.label.toLowerCase().includes(q))
      : valueOptions;
  }, [valueOptions, valueSearch]);

  const selectedValueLabels = draft
    ? valueOptions.filter((o) => draft.has(o.value)).map((o) => o.label)
    : [];

  const selectAllVisible = () =>
    setDrafts((prev) => {
      const values = new Set(prev[selectedKey]);
      visibleOptions.forEach((o) => values.add(o.value));
      return { ...prev, [selectedKey]: values };
    });

  const clearCurrent = () =>
    setDrafts((prev) => ({ ...prev, [selectedKey]: new Set() }));

  const handleReset = () => setDrafts(buildDrafts(false));

  const handleApply = () => {
    const built: PropertyFilter[] = [];
    FILTER_DEFS.forEach((d) => {
      const values = drafts[d.key];
      if (!values || values.size === 0) return;
      const existing = filters.find((f) => f.key === d.key);
      built.push({
        id: existing?.id ?? makeFilterId(),
        key: d.key,
        label: d.label,
        kind: d.kind,
        enabled: existing?.enabled ?? true,
        values: Array.from(values),
      });
    });
    onApply(built);
    onClose();
  };

  return (
    <ComposedModal open={open} onClose={onClose} size="lg">
      <ModalHeader title="Properties Filters" />
      <ModalBody hasScrollingContent>
        <div className={style.body}>
          <div className={style.listCol}>
            <Search
              size="sm"
              labelText="Search Filter Names"
              placeholder="Search Filter Names"
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
            />
            <ul className={style.list}>
              {filterList.map((f) => (
                <li key={f.key}>
                  <button
                    type="button"
                    className={`${style.listItem} ${
                      f.key === selectedKey ? style.listItemActive : ''
                    }`}
                    onClick={() => {
                      setSelectedKey(f.key);
                      setValueSearch('');
                    }}
                  >
                    {f.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className={style.detailCol}>
            <h3 className={style.detailTitle}>{def.label}</h3>

            <p className={style.selectedLabel}>Properties</p>
            {selectedValueLabels.length === 0 ? (
              <p className={style.emptyValues}>No filters selected</p>
            ) : (
              <div className={style.chips}>
                {selectedValueLabels.map((label) => (
                  <span key={label} className={style.chip}>
                    {label}
                  </span>
                ))}
              </div>
            )}
            <hr className={style.divider} />
            <Search
              size="sm"
              labelText={`Search ${def.label}`}
              placeholder={`Search ${def.label.toLowerCase()}...`}
              value={valueSearch}
              onChange={(e) => setValueSearch(e.target.value)}
            />
            <div className={style.valuesHeader}>
              <Button kind="ghost" size="sm" onClick={selectAllVisible}>
                Select All
              </Button>
              <span className={style.resultsHint}>
                {format0(visibleOptions.length)} results shown
              </span>
            </div>
            {def.kind === 'difference' && (
              <p className={style.detailDesc}>
                Show only properties with differences in the checked fields.
              </p>
            )}
            <div className={style.valueList}>
              {visibleOptions.map((o) => (
                <Checkbox
                  key={o.value}
                  id={`filter-value-${selectedKey}-${o.value}`}
                  labelText={
                    o.count !== undefined
                      ? `${o.label} (${format0(o.count)})`
                      : o.label
                  }
                  checked={draft?.has(o.value) ?? false}
                  onChange={(_evt, { checked }: { checked: boolean }) =>
                    toggleValue(o.value, checked)
                  }
                />
              ))}
            </div>
            <div className={style.clearRow}>
              <Button kind="ghost" size="sm" onClick={clearCurrent}>
                Clear
              </Button>
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button kind="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button kind="ghost" onClick={handleReset}>
          Reset
        </Button>
        <Button kind="primary" onClick={handleApply}>
          Apply Filters
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
};
